import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { CircleHelp, X } from "lucide-react";
import type { PreparationStep } from "./lib/workflow";
import type { ServiceTab } from "./components/ServiceSettings";
import { AuthProvider, loginDestination, useAuth } from "./lib/auth";
import { Login, LoginLoading } from "./components/Login";
const BenchmarkAccounts = lazy(() =>
  import("./components/BenchmarkAccounts").then((m) => ({
    default: m.BenchmarkAccounts,
  })),
);
const Landing = lazy(() =>
  import("./components/Landing").then((m) => ({ default: m.Landing })),
);
const WorkspaceHome = lazy(() =>
  import("./components/WorkspaceHome").then((m) => ({
    default: m.WorkspaceHome,
  })),
);
const StoryboardStart = lazy(() =>
  import("./components/StoryboardStart").then((m) => ({
    default: m.StoryboardStart,
  })),
);
const StoryboardFlow = lazy(() =>
  import("./components/StoryboardFlow").then((m) => ({
    default: m.StoryboardFlow,
  })),
);
const Studio = lazy(() =>
  import("./components/Studio").then((m) => ({ default: m.Studio })),
);
const ServiceSettings = lazy(() =>
  import("./components/ServiceSettings").then((m) => ({
    default: m.ServiceSettings,
  })),
);
const ViralWorkbench = lazy(() =>
  import("./components/ResearchWorkspace").then((m) => ({
    default: m.ViralWorkbench,
  })),
);
const ScriptLibrary = lazy(() =>
  import("./components/ScriptLibrary").then((m) => ({
    default: m.ScriptLibrary,
  })),
);
const RecycleBin = lazy(() =>
  import("./components/RecycleBin").then((m) => ({ default: m.RecycleBin })),
);
const TaskCenter = lazy(() =>
  import("./components/TaskCenter").then((m) => ({ default: m.TaskCenter })),
);
const HotRadar = lazy(() =>
  import("./components/HotRadar").then((m) => ({ default: m.HotRadar })),
);

export default function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

function AuthGate() {
  const auth = useAuth()!;
  const [route, setRoute] = useState(() =>
    location.hash.startsWith("#/") ? location.hash.slice(1) : "/",
  );
  useEffect(() => {
    const change = () => {
      const hash = location.hash.slice(1);
      setRoute(hash.startsWith("/") ? hash : "/");
      if (hash.startsWith("/") || !hash) window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", change);
    return () => window.removeEventListener("hashchange", change);
  }, []);
  const login = route.split("?")[0] === "/login";
  useEffect(() => {
    if (auth.loading || route === "/") return;
    if (!auth.user && !login)
      location.replace("#/login?next=" + encodeURIComponent(route));
    else if (auth.user && login) location.replace("#" + loginDestination());
  }, [auth.loading, auth.user, login, route]);
  if (route === "/")
    return (
      <Suspense
        fallback={
          <div role="status" style={{ padding: 32 }}>
            正在加载…
          </div>
        }
      >
        <Landing />
      </Suspense>
    );
  if (auth.loading) return <LoginLoading />;
  if (!auth.user) return <Login />;
  if (login) return <LoginLoading />;
  return <WorkspaceApp key={auth.user.id} route={route} />;
}

function WorkspaceApp({ route }: { route: string }) {
  const auth = useAuth()!;
  const legacyLibraryRoute = /^\/projects(?:\/projects)?\/?$/.test(route);
  useEffect(() => {
    if (legacyLibraryRoute) location.replace("#/scripts");
  }, [legacyLibraryRoute]);
  const openSettings = useCallback(() => {
    location.hash = "/settings/model";
  }, []);
  useEffect(() => {
    if (
      route.startsWith("/settings") &&
      !/^\/settings\/(model|speech|radar|runtime|storage|performance|storyboard)$/.test(
        route,
      )
    ) {
      location.replace("#/settings/model");
    }
  }, [route]);
  const [radarSettingsRevision, setRadarSettingsRevision] = useState(0);
  const [toast, setToast] = useState("");
  const notify = useCallback((s: string) => setToast(s), []);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 5500);
    return () => clearTimeout(timer);
  }, [toast]);
  return (
    <>
      <Suspense
        fallback={
          <div role="status" style={{ padding: 32 }}>
            正在加载…
          </div>
        }
      >
        {route.startsWith("/settings") ? (
          auth.user?.role !== "admin" ? (
            <div className="login-loading">
              <p>系统服务配置由工作台管理员管理。</p>
              <button
                className="button"
                onClick={() => (location.hash = "/projects")}
              >
                返回工作台
              </button>
            </div>
          ) : (
            <ServiceSettings
              tab={(route.split("/")[2] || "model") as ServiceTab}
              onTabChange={(tab) => (location.hash = "/settings/" + tab)}
              onRadarChanged={() =>
                setRadarSettingsRevision((value) => value + 1)
              }
            />
          )
        ) : route.startsWith("/tasks") ? (
          <TaskCenter onSettings={openSettings} notify={notify} />
        ) : route.startsWith("/recycle-bin") ? (
          <RecycleBin onSettings={openSettings} />
        ) : route.startsWith("/scripts") || legacyLibraryRoute ? (
          <ScriptLibrary onSettings={openSettings} />
        ) : route.startsWith("/benchmarks") ? (
          <BenchmarkAccounts onSettings={openSettings} />
        ) : route.startsWith("/radar") ? (
          <HotRadar
            notify={notify}
            onSettings={openSettings}
            settingsRevision={radarSettingsRevision}
          />
        ) : route.startsWith("/viral") ? (
          <ViralWorkbench
            key={route}
            analysisId={
              new URLSearchParams(route.split("?")[1] || "").get("analysis") ||
              ""
            }
            onSettings={openSettings}
            notify={notify}
          />
        ) : /^\/storyboard\/(import|narration|review|compare)\//.test(route) ? (
          <StoryboardFlow
            key={route}
            projectId={route.split("?")[0].split("/")[3]}
            step={route.split("/")[2] as PreparationStep}
            notify={notify}
            onSettings={openSettings}
          />
        ) : route.startsWith("/storyboard/") ? (
          <StoryboardStart
            key={route}
            notify={notify}
            onSettings={openSettings}
          />
        ) : route.startsWith("/studio/") ? (
          <Studio
            key={route.split("/")[2]}
            projectId={route.split("/")[2]}
            notify={notify}
            onSettings={openSettings}
          />
        ) : route.startsWith("/projects") ? (
          <WorkspaceHome
            key={route}
            onImport={() => (location.hash = "/storyboard/new")}
            onSettings={openSettings}
            notify={notify}
          />
        ) : (
          <Landing />
        )}
      </Suspense>
      <div className="toast-region" aria-live="polite">
        {toast && (
          <div className="toast">
            <CircleHelp size={18} />
            <span>{toast}</span>
            <button onClick={() => setToast("")} aria-label="关闭提示">
              <X size={15} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
